import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  useDeleteApiTarsByIdMutation,
  useGetApiTarsByIdQuery,
  useGetApiUsersMeQuery,
  usePostApiTarsByIdSubmitMutation,
} from '../../app/athenaApiGen';
import DynamicForm from '../../components/dynamic-form/DynamicForm';
import DeleteConfirmation from '../../components/Modals/DeleteConfirmation';
import {
  createFormDataChangeHandler,
  parseRawFormData,
  toNumber,
} from '../../dashboard/components/formUtils';
import useSchemaForm from '../../hooks/useSchemaForm';
import { tarFormSchema } from './TARFormSchema';

const DEBUG_MODE = true; // Toggle this for debug features

export default function TARForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: meData } = useGetApiUsersMeQuery();
  const [formData, setFormData, isLoading, resetFormData] =
    useSchemaForm('TARFormSchema');
  const { data: tarResp, isFetching: isFetchingTAR } = useGetApiTarsByIdQuery(
    { id: id || '' },
    { skip: !id },
  );
  const [deleteTar, { isLoading: deleting }] = useDeleteApiTarsByIdMutation();
  const [submitTar] = usePostApiTarsByIdSubmitMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isCloneMode = searchParams.get('mode') === 'clone';
  const [savedTarId, setSavedTarId] = React.useState(isCloneMode ? null : (id || null)); // Store ID after save

  // Always start fresh on no-id route navigations like /tar
  useEffect(() => {
    if (id) return;
    resetFormData();
    setSavedTarId(null);
    setDeleteDialogOpen(false);
    setSnackbar({ open: false, message: '', severity: 'success' });
  }, [id, location.key, resetFormData]);

  const parsedRaw = useMemo(() => {
    const raw = tarResp?.rawFormData || tarResp?.RawFormData;
    return raw ? parseRawFormData(raw) : null;
  }, [tarResp]);

  // Initialize itinerary with one default traveler (expenses will be auto-initialized by ExpenseRows component)
  useEffect(() => {
    if (id) return;
    setFormData((prev) => {
      const itineraryItems = prev?.itinerary;
      if (Array.isArray(itineraryItems) && itineraryItems.length > 0)
        return prev;
      return {
        ...prev,
        itinerary: [
          {
            contactId: '',
            travelerName: '',
            origin: '',
            departureDate: null,
            departureTime: '',
            destination: '',
            arrivalDate: null,
            arrivalTime: '',
            expenses: [], // ExpenseRows will initialize this
          },
        ],
      };
    });
  }, [id, location.key, setFormData]);

  // Compute subtotal, totalNTETripCost, and balances reactively
  // ExpenseRows component handles individual expense calculations
  useEffect(() => {
    const travelers = formData?.itinerary || [];

    // Aggregate all expense totals from all travelers
    let allExpenses = [];
    travelers.forEach((traveler) => {
      const expenses = traveler?.expenses || [];
      allExpenses.push(...expenses);
    });

    // Calculate subtotal from all expenses across all travelers
    const lineTotals = allExpenses.map((r) => toNumber(r.totalEstimatedCost));
    const subtotal = lineTotals.reduce((a, b) => a + b, 0);
    const indirect = toNumber(formData?.indirectHandlingCost);
    const ga = toNumber(formData?.gaCost);
    const total = subtotal + indirect + ga;

    const clinValue = toNumber(formData?.clinx002Value);
    const cumulative = toNumber(formData?.cumulativeAmountBilled);
    const newBal = clinValue - (cumulative + total);

    // Only update state when values actually change to avoid loops
    setFormData((prev) => {
      const next = { ...prev };
      const nextSubtotal = subtotal ? String(subtotal.toFixed(2)) : '';
      const nextTotal = total ? String(total.toFixed(2)) : '';
      const nextNewBal = String(newBal.toFixed(2));
      if (next.subtotal !== nextSubtotal) next.subtotal = nextSubtotal;
      if (next.totalNTETripCost !== nextTotal)
        next.totalNTETripCost = nextTotal;
      if (next.tarEstimate !== nextTotal)
        next.tarEstimate = nextTotal;
      // currentCLINX002Balance can be derived as clinValue - cumulative
      const currentBal = clinValue - cumulative;
      const nextCurrentBal = String(currentBal.toFixed(2));
      if (next.currentCLINX002Balance !== nextCurrentBal)
        next.currentCLINX002Balance = nextCurrentBal;
      if (next.newCLINX002Balance !== nextNewBal)
        next.newCLINX002Balance = nextNewBal;
      return next;
    });
  }, [
    formData?.itinerary,
    formData?.indirectHandlingCost,
    formData?.gaCost,
    formData?.clinx002Value,
    formData?.cumulativeAmountBilled,
    setFormData,
  ]);

  useEffect(() => {
    if (!id) return;
    if (!parsedRaw && tarResp) {
      const normalized = { ...tarResp };
      // Convert date fields to dayjs objects regardless of source
      if (normalized.tarDate) normalized.tarDate = dayjs(normalized.tarDate);
      if (normalized.travelDate)
        normalized.travelDate = dayjs(normalized.travelDate);
      if (normalized.dueDate) normalized.dueDate = dayjs(normalized.dueDate);

      // Map backend itineraries to form itinerary structure
      if (
        Array.isArray(normalized.itineraries) &&
        normalized.itineraries.length > 0
      ) {
        normalized.itinerary = normalized.itineraries.map((itin, index) => {
          // Extract estimates and convert to expenses format
          const estimate = Array.isArray(itin.estimates) && itin.estimates[0];
          const lineItems = estimate?.lineItems || [];

          // Initialize expense rows - now with separate perDiem row
          const expenses = [
            {
              type: 'perDiem',
              expense: '',
              organizationName: '',
              numberOfDays: '',
              itemUnitCost: '',
              totalEstimatedCost: '',
              fixed: true,
            },
            {
              type: 'hotel',
              expense: itin.hotelId || '',
              organizationName: '',
              numberOfDays: '',
              itemUnitCost: '',
              totalEstimatedCost: '',
              fixed: true,
            },
            {
              type: 'car',
              expense: itin.rentalId || '',
              organizationName: '',
              numberOfDays: '',
              itemUnitCost: '',
              totalEstimatedCost: '',
              fixed: true,
            },
            {
              type: 'airfare',
              expense: itin.airlineId || '',
              organizationName: '',
              numberOfDays: '',
              itemUnitCost: '',
              totalEstimatedCost: '',
              fixed: true,
            },
            {
              type: 'other',
              expense: '',
              organizationName: '',
              numberOfDays: '',
              itemUnitCost: '',
              totalEstimatedCost: '',
              fixed: false,
            },
          ];

          // Populate expense values from line items
          lineItems.forEach((item) => {
            const desc = item.description?.toUpperCase() || '';

            switch (desc) {
              case 'PER DIEM': {
                const perDiemExp = expenses.find((e) => e.type === 'perDiem');
                if (perDiemExp) {
                  perDiemExp.numberOfDays = String(item.days || '');
                  perDiemExp.itemUnitCost = String(item.unitCost || '');
                  perDiemExp.totalEstimatedCost = String(
                    item.totalLineItemEstimate || '',
                  );
                }
                break;
              }
              case 'HOTEL': {
                const hotelExp = expenses.find((e) => e.type === 'hotel');
                if (hotelExp) {
                  hotelExp.numberOfDays = String(item.days || '');
                  hotelExp.itemUnitCost = String(item.unitCost || '');
                  hotelExp.totalEstimatedCost = String(
                    item.totalLineItemEstimate || '',
                  );
                }
                break;
              }
              case 'CAR RENTAL': {
                const carExp = expenses.find((e) => e.type === 'car');
                if (carExp) {
                  carExp.numberOfDays = String(item.days || '');
                  carExp.itemUnitCost = String(item.unitCost || '');
                  carExp.totalEstimatedCost = String(
                    item.totalLineItemEstimate || '',
                  );
                }
                break;
              }
              case 'AIRFARE': {
                const airExp = expenses.find((e) => e.type === 'airfare');
                if (airExp) {
                  airExp.totalEstimatedCost = String(
                    item.totalLineItemEstimate || '',
                  );
                }
                break;
              }
              case 'OTHER': {
                const otherExp = expenses.find((e) => e.type === 'other');
                if (otherExp) {
                  otherExp.numberOfDays = String(item.days || '');
                  otherExp.itemUnitCost = String(item.unitCost || '');
                  otherExp.totalEstimatedCost = String(
                    item.totalLineItemEstimate || '',
                  );
                }
                break;
              }
            }
          });

          // Map backend legs to form legs structure
          // If backend has no legs, don't set legs property - let RepeatableSection initialize with minItems
          const legs =
            Array.isArray(itin.legs) && itin.legs.length > 0
              ? itin.legs.map((leg, legIndex) => ({
                  _id: `leg-backend-${itin.id || index}-${legIndex}-${Date.now()}`,
                  __placeholder: false,
                  originLocationId: leg.originLocationId || '',
                  departure: leg.departure ? dayjs(leg.departure) : null,
                  destinationLocationId: leg.destinationLocationId || '',
                  arrival: leg.arrival ? dayjs(leg.arrival) : null,
                }))
              : undefined; // undefined lets RepeatableSection handle initialization

          const travelerData = {
            _id: `itinerary-backend-${itin.id || index}-${Date.now()}`, // Stable ID for RepeatableSection
            __placeholder: false, // Mark as real data, not placeholder
            contactId: itin.contactId || itin.travelerId || '',
            travelerName: '', // Will be populated by autocomplete
            expenses,
          };

          // Only include legs if we have data for them
          if (legs !== undefined) {
            travelerData.legs = legs;
          }

          return travelerData;
        });
        delete normalized.itineraries; // Remove backend field
      } else if (Array.isArray(normalized.itinerary)) {
        // Normalize nested itinerary dates if present (for rawFormData structure)
        normalized.itinerary = normalized.itinerary.map((t, index) => ({
          _id: t._id || `itinerary-form-${index}-${Date.now()}`, // Preserve or add stable ID
          __placeholder: t.__placeholder ?? false, // Preserve placeholder flag
          contactId: t.contactId || t.travelerId || '',
          travelerName: t.travelerName || t.userName || '',
          legs: Array.isArray(t.legs)
            ? t.legs.map((leg, legIndex) => ({
                _id: leg._id || `leg-form-${index}-${legIndex}-${Date.now()}`,
                __placeholder: leg.__placeholder ?? false,
                originLocationId: leg.originLocationId || '',
                departure: leg.departure ? dayjs(leg.departure) : null,
                destinationLocationId: leg.destinationLocationId || '',
                arrival: leg.arrival ? dayjs(leg.arrival) : null,
              }))
            : [],
          expenses: Array.isArray(t.expenses) ? t.expenses : [],
        }));
      }
      normalized.workflowState = isCloneMode ? '' : (tarResp.workflowState ?? '');
      setFormData((prev) => ({ ...prev, ...normalized }));
      return;
    }
    if (parsedRaw) {
      const normalized = { ...parsedRaw };
      if (normalized.tarDate) normalized.tarDate = dayjs(normalized.tarDate);
      if (normalized.travelDate)
        normalized.travelDate = dayjs(normalized.travelDate);
      if (normalized.dueDate) normalized.dueDate = dayjs(normalized.dueDate);
      if (Array.isArray(normalized.itinerary)) {
        normalized.itinerary = normalized.itinerary.map((t, tIndex) => {
          const traveler = {
            _id: t._id || `itinerary-raw-${tIndex}-${Date.now()}`,
            __placeholder: t.__placeholder ?? false,
            contactId: t.contactId || t.travelerId || '',
            travelerName: t.travelerName || t.userName || '',
            expenses: Array.isArray(t.expenses) ? t.expenses : [],
          };

          // Handle nested legs if present in rawFormData
          if (Array.isArray(t.legs) && t.legs.length > 0) {
            traveler.legs = t.legs.map((leg, legIndex) => ({
              _id: leg._id || `leg-raw-${tIndex}-${legIndex}-${Date.now()}`,
              __placeholder: leg.__placeholder ?? false,
              originLocationId: leg.originLocationId || '',
              departure: leg.departure ? dayjs(leg.departure) : null,
              destinationLocationId: leg.destinationLocationId || '',
              arrival: leg.arrival ? dayjs(leg.arrival) : null,
            }));
          }

          return traveler;
        });
      }
      normalized.workflowState = isCloneMode ? '' : (tarResp?.workflowState ?? '');
      setFormData((prev) => ({ ...prev, ...normalized }));
    }
  }, [id, isCloneMode, parsedRaw, tarResp, setFormData]);

  if (isLoading || (id && isFetchingTAR && !parsedRaw)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleFormDataChange = createFormDataChangeHandler(setFormData);

  // Default to view mode when editing an existing record (id present),
  // unless explicitly set to edit via ?mode=edit.
  // Also force view mode if workflow has progressed past Pending.
  const editableWorkflow = !formData?.workflowState || formData.workflowState === 'Pending';
  const isViewMode = isCloneMode ? false : (id ? searchParams.get('mode') !== 'edit' || !editableWorkflow : false);

  // Handle cancel — honor an explicit returnTo from the navigating link
  // (e.g. View Form from the library ViewPanel) first. Otherwise act like
  // the browser back button so callers from the approval queue, home
  // widget, or any future entry point land back on the page they came
  // from instead of being forced to the TAR list.
  const handleCancel = () => {
    const returnTo = location.state?.returnTo;
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(-1);
    }
  };

  // Handle successful submission - navigate back to list
  const handleSubmitSuccess = (result) => {
    if (result.ok && result.payload) {
      // Store the returned ID for subsequent saves
      if (result.payload.tarId) {
        setSavedTarId(result.payload.tarId);
      }
    }
    if (result.ok && !result.skipNavigation) {
      setTimeout(() => navigate('/tar-list'), 2500);
    }
  };

  // Handle delete
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTar({ id: id }).unwrap();
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'TAR deleted successfully',
        severity: 'success',
      });
      setTimeout(() => navigate('/tar-list'), 2500);
    } catch (error) {
      console.error('Failed to delete TAR', error);
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Failed to delete TAR. Please try again.',
        severity: 'error',
      });
    }
  };

  const canDelete = id && editableWorkflow && formData?.workflowState !== undefined;

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '100%' } }}>
      <DynamicForm
        schema={tarFormSchema}
        formData={formData}
        setFormData={handleFormDataChange}
        onSubmit={handleSubmitSuccess}
        showSubmit={isCloneMode || !savedTarId}
        isEditMode={!isCloneMode && Boolean(savedTarId)}
        recordId={isCloneMode ? null : savedTarId}
        readOnly={isViewMode}
        onCancel={handleCancel}
        onDelete={canDelete ? handleDeleteClick : undefined}
        submitMutation={submitTar}
        authorId={meData?.contact?.contactId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        open={deleteDialogOpen}
        title="Delete TAR"
        onClose={() => setDeleteDialogOpen(false)}
        content={{
          mainText: `
                              Are you sure you want to delete TAR <strong>${formData?.tarNumber}</strong>?
                              <br /><br />
                              This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const validateFormData = (data) => {
  const errors = [];

  // Required fields validation
  const requiredFields = ['tarNumber', 'tarDate', 'destination'];
  requiredFields.forEach((field) => {
    if (!data[field]) {
      errors.push(`${field} is required`);
    }
  });

  // Validate date ranges
  if (data.departureDate && data.returnDate) {
    if (new Date(data.departureDate) > new Date(data.returnDate)) {
      errors.push('Departure date must be before return date');
    }
  }

  return errors;
};
